import { z } from "zod/v4";

const endpoint = <TInput, TOutput, TAuth extends boolean>(def: {
  input: TInput;
  output: TOutput;
  authRequired: TAuth;
}) => def;

const mediaAttachmentSchema = z.union([
  z.object({
    kind: z.literal("inline"),
    mimeType: z.string(),
    dataBase64: z.string(),
    caption: z.string().optional(),
  }),
  z.object({
    kind: z.literal("file"),
    mimeType: z.string(),
    fileUri: z.string(),
    caption: z.string().optional(),
  }),
]);

const preferredNetworkValues = [
  "alice-and-bot",
  "email",
  "facebook-messenger",
  "instagram",
  "phone-call",
  "slack",
  "sms",
  "teams",
  "telegram",
  "telegram-demo",
  "whatsapp",
  "whatsapp-demo",
  "whatsapp-demo-business-integration",
  "whatsapp-for-business",
] as const;

export const knownGeminiVoices = [
  "Aoede",
  "Charon",
  "Fenrir",
  "Kore",
  "Puck",
  "Zephyr",
  "Orus",
] as const;

export type GeminiVoice = (typeof knownGeminiVoices)[number];

const remoteToolSchema = z.object({
  name: z.string().describe("Tool name the agent uses to invoke this tool."),
  description: z.string().describe(
    "Human-readable description of what the tool does.",
  ),
  url: z.string().url().refine(
    (u) => /^https?:\/\//.test(u),
    "URL must start with http:// or https:// (do not include the HTTP method)",
  ).describe("HTTPS endpoint the bot POSTs to when calling."),
  parameters: z.any().describe(
    "JSON Schema object describing the tool's input parameters.",
  ),
});

const remoteSkillSchema = z.object({
  name: z.string().describe("Skill name."),
  description: z.string().describe(
    "Human-readable description of the skill.",
  ),
  instructions: z.string().describe(
    "Instructions the agent receives when using this skill.",
  ),
  tools: z.array(remoteToolSchema).describe(
    "HTTP tool endpoints grouped under this skill.",
  ),
});

const botAuthFields = {
  secret: z.string().optional().describe(
    "Webhook secret for legacy bot authentication.",
  ),
  apiToken: z.string().optional().describe(
    "API token (starts with p2b_).",
  ),
  botId: z.string().optional().describe(
    "Bot ID.",
  ),
};

const requireBotAuth = (value: {
  secret?: string;
  apiToken?: string;
  botId?: string;
}) => !!value.secret || (!!value.apiToken && !!value.botId);

const botAuthRefineConfig = {
  message: "Provide either 'secret' or both 'apiToken' and 'botId'.",
  path: ["secret"],
};

const agentTestOutcomeSchema = z.union([
  z.object({
    success: z.boolean(),
    reason: z.string(),
    turns: z.number(),
    transcript: z.string(),
  }),
  z.object({ success: z.literal(false), error: z.string() }),
]);

export type AgentTestOutcome = z.infer<typeof agentTestOutcomeSchema>;

const whatsappTemplateComponentSchema = z.object({
  type: z.enum(["HEADER", "BODY", "FOOTER", "BUTTONS"]),
  format: z.enum(["TEXT", "IMAGE", "VIDEO", "DOCUMENT", "LOCATION"])
    .optional().describe("Required for HEADER. Usually TEXT."),
  text: z.string().optional().describe(
    "Required for BODY, FOOTER, and TEXT HEADER. Use {{1}}, {{2}} for variables.",
  ),
  example: z.object({
    body_text: z.array(z.array(z.string())).optional().describe(
      'Example values for BODY variables. Shape: [["value for {{1}}", "value for {{2}}"]].',
    ),
    header_text: z.array(z.string()).optional(),
    header_handle: z.array(z.string()).optional(),
  }).optional(),
  buttons: z.array(z.object({
    type: z.enum(["QUICK_REPLY", "URL", "PHONE_NUMBER"]),
    text: z.string(),
    url: z.string().optional(),
    phone_number: z.string().optional(),
    example: z.array(z.string()).optional(),
  })).optional(),
});

const whatsappTemplateSchema = z.object({
  name: z.string(),
  language: z.string(),
  status: z.string(),
  category: z.string(),
  id: z.string().optional(),
  rejected_reason: z.string().optional(),
});

const whatsappTemplateResultSchema = z.union([
  z.object({
    success: z.literal(true),
    templates: z.array(whatsappTemplateSchema),
  }),
  z.object({ success: z.literal(false), error: z.string() }),
]);

const createRemoteActionInputSchema = z
  .object({
    ...botAuthFields,
    targetConversationId: z.string().optional().describe(
      "Optional explicit target conversation ID override (e.g. a WhatsApp group id that ends with @g.us).",
    ),
    aliceChatUserId: z.string().optional().describe(
      "If preferredNetwork is 'alice-and-bot', you can provide an explicit user ID (their publicSignKey) here. Omit for anonymous users if you just have the conversation ID.",
    ),
    description: z.string().describe(
      "Description of what needs to be done, including context of why and who this is for. Formulate as an internal thought.",
    ),
    preferredNetwork: z.enum(preferredNetworkValues).optional().describe(
      `Which messaging network to use for the scheduled action, valid options are: ${
        preferredNetworkValues.join(", ")
      }. Omit for internal thread (no external message sent).`,
    ),
    recurrenceRule: z.string().optional().describe(
      "Recurrence rule for the scheduled action. Accepts: 'daily', 'weekly', 'monthly', a number (ms), or a full RFC 5545 RRULE string (e.g., 'RRULE:FREQ=DAILY'). Prefer the RRULE format for advanced recurrence. Leave empty for non-recurring tasks.",
    ),
    recurrenceEnd: z.string().optional().describe(
      "End date/time for recurrence (optional, in local time format).",
    ),
    runAt: z.number().optional(),
    contactPhone: z.string().optional().describe(
      "Phone number to call or send SMS to (e.g. +1234567890 or 972501234567).",
    ),
    contactWhatsAppNumber: z.string().optional().describe(
      "WhatsApp phone number including country code, digits only (e.g. 972501234567).",
    ),
    contactTelegramId: z.string().optional().describe(
      "Telegram user ID (numeric string) to message.",
    ),
    contactSlackId: z.string().optional().describe(
      "Slack user ID or conversation ID to message.",
    ),
    contactTeamsId: z.string().optional().describe(
      "Microsoft Teams user ID to message.",
    ),
    contactAliceAndBotHandle: z.string().optional().describe(
      "Alice-and-Bot handle to message (human-friendly alias, no spaces).",
    ),
    contactAliceAndBotPublicId: z.string().optional().describe(
      "Alice-and-Bot public id (public signing key) to message.",
    ),
    contactEmail: z.string().optional().describe(
      "Email address of the contact.",
    ),
    contactName: z.string().optional().describe(
      "Optional friendly display name of the contact/user (e.g. 'Sarah', 'Uri').",
    ),
    subject: z.string().optional().describe(
      "Email subject line. Required when preferredNetwork is 'email', must not be provided otherwise.",
    ),
    attachments: z.array(mediaAttachmentSchema).optional().describe(
      "Optional media attachments (images, files, etc.) to include with the task description. Only used when preferredNetwork is omitted (internal thread).",
    ),
    whatsappTemplateName: z.string().optional().describe(
      "Approved WhatsApp Business template name to send. Only used when preferredNetwork is 'whatsapp' on a bot connected via the Official WhatsApp Business API. The template (with this name + whatsappTemplateLanguage) must already be APPROVED in Meta. If omitted, the bot's default task template is used.",
    ),
    whatsappTemplateLanguage: z.string().optional().describe(
      "Language code (e.g. en_US) of the approved WhatsApp Business template identified by whatsappTemplateName. Required when whatsappTemplateName is set.",
    ),
    whatsappTemplateVariables: z.array(z.string()).optional().describe(
      "Positional variable values for the WhatsApp Business template body, filling {{1}}, {{2}}, ... in order. Meta API strictly prohibits newlines (\\n), tabs (\\t), or 4+ consecutive spaces in parameter text. If omitted, the action description is used as the single {{1}} variable.",
    ),
    whatsappTemplateHeaderImageUrl: z.string().optional().describe(
      "Optional header image URL when sending a WhatsApp Business template that has an IMAGE header component.",
    ),
    phoneCallVoice: z.enum(knownGeminiVoices).optional().describe(
      "Voice to use when preferredNetwork is 'phone-call'. Male options: 'Charon' (calm/baritone), 'Puck' (upbeat/tenor), 'Fenrir'. Female options: 'Aoede' (breezy/soprano), 'Kore' (firm/soprano). Select a voice fitting your gender and persona.",
    ),
    phoneCallLanguage: z.string().optional().describe(
      "Spoken language for the phone call (e.g. 'he', 'en', 'ar', 'es'). Match the recipient's and context's language.",
    ),
    phoneCallGreeting: z.string().optional().describe(
      "Opening spoken greeting to say immediately when the user answers. Must be 1 short, natural sentence in the call language.",
    ),
  })
  .refine(
    (value) =>
      [
        value.contactPhone,
        value.contactWhatsAppNumber,
        value.contactTelegramId,
        value.contactSlackId,
        value.contactTeamsId,
        value.contactAliceAndBotHandle,
        value.contactAliceAndBotPublicId,
        value.contactEmail,
      ]
        .filter((identifier) => identifier && identifier.trim().length > 0)
        .length <= 1,
    {
      message:
        "Provide at most one of contactPhone, contactWhatsAppNumber, contactTelegramId, contactSlackId, contactTeamsId, contactAliceAndBotHandle, contactAliceAndBotPublicId, contactEmail.",
      path: ["contactPhone"],
    },
  )
  .refine(
    (value) =>
      !value.preferredNetwork ||
      value.preferredNetwork !== "email" || (!!value.subject &&
        value.subject.trim().length > 0),
    {
      message: "subject is required when preferredNetwork is 'email'.",
      path: ["subject"],
    },
  )
  .refine(
    (value) =>
      !value.preferredNetwork || value.preferredNetwork === "email" ||
      !value.subject,
    {
      message:
        "subject must not be provided when preferredNetwork is not 'email'.",
      path: ["subject"],
    },
  )
  .refine(requireBotAuth, botAuthRefineConfig)
  .refine(
    (value) =>
      !value.whatsappTemplateName ||
      (!!value.whatsappTemplateLanguage &&
        value.preferredNetwork === "whatsapp"),
    {
      message:
        "whatsappTemplateName requires whatsappTemplateLanguage and preferredNetwork='whatsapp'.",
      path: ["whatsappTemplateName"],
    },
  );

const injectContextInputSchema = z
  .object({
    ...botAuthFields,
    context: z.string().describe(
      "The context string to inject into the conversation as an own_thought. The agent will act on this.",
    ),
    toolCallId: z.string().optional().describe(
      "If provided, the context is injected as a tool_result instead of own_thought, preventing hallucinations from async tools.",
    ),
    preferredNetwork: z.enum(preferredNetworkValues).describe(
      `Which messaging network the conversation is on, valid options are: ${
        preferredNetworkValues.join(", ")
      }`,
    ),
    targetConversationId: z.string().describe(
      "The exact target conversation ID. For WhatsApp groups use the group ID ending with @g.us, for 1:1 WhatsApp use the ID ending with @c.us.",
    ),
    aliceChatUserId: z.string().optional().describe(
      "If preferredNetwork is 'alice-and-bot', you can provide an explicit user ID (their publicSignKey) here. Omit for anonymous users if you just have the conversation ID.",
    ),
    contactAliceAndBotPublicId: z.string().optional().describe(
      "Alice-and-Bot public id (public signing key) to target.",
    ),
    subject: z.string().optional().describe(
      "Email subject line. Required when preferredNetwork is 'email', must not be provided otherwise.",
    ),
    attachments: z.array(mediaAttachmentSchema).optional().describe(
      "Optional media attachments (images, files, etc.) to include with the injected context. Passed through to the agent so it can reason over non-text content.",
    ),
  })
  .refine(
    (value) =>
      value.preferredNetwork !== "email" || (!!value.subject &&
        value.subject.trim().length > 0),
    {
      message: "subject is required when preferredNetwork is 'email'.",
      path: ["subject"],
    },
  )
  .refine(
    (value) => value.preferredNetwork === "email" || !value.subject,
    {
      message:
        "subject must not be provided when preferredNetwork is not 'email'.",
      path: ["subject"],
    },
  )
  .refine(requireBotAuth, botAuthRefineConfig);

const injectReplyInputSchema = z
  .object({
    ...botAuthFields,
    text: z.string().describe(
      "The message text to send to the user.",
    ),
    preferredNetwork: z.enum(preferredNetworkValues).describe(
      `Which messaging network the conversation is on, valid options are: ${
        preferredNetworkValues.join(", ")
      }`,
    ),
    targetConversationId: z.string().describe(
      "The exact target conversation ID. For WhatsApp groups use the group ID ending with @g.us, for 1:1 WhatsApp use the ID ending with @c.us.",
    ),
    aliceChatUserId: z.string().optional().describe(
      "If preferredNetwork is 'alice-and-bot', you can provide an explicit user ID (their publicSignKey) here. Omit for anonymous users if you just have the conversation ID.",
    ),
    contactAliceAndBotPublicId: z.string().optional().describe(
      "Alice-and-Bot public id (public signing key) to target.",
    ),
    subject: z.string().optional().describe(
      "Email subject line. Required when preferredNetwork is 'email', must not be provided otherwise.",
    ),
    attachments: z.array(mediaAttachmentSchema).optional().describe(
      "Optional media attachments (images, files, etc.) to send to the user.",
    ),
  })
  .refine(
    (value) =>
      value.preferredNetwork !== "email" || (!!value.subject &&
        value.subject.trim().length > 0),
    {
      message: "subject is required when preferredNetwork is 'email'.",
      path: ["subject"],
    },
  )
  .refine(
    (value) => value.preferredNetwork === "email" || !value.subject,
    {
      message:
        "subject must not be provided when preferredNetwork is not 'email'.",
      path: ["subject"],
    },
  )
  .refine(requireBotAuth, botAuthRefineConfig);

const scheduledTaskSchema = z.object({
  id: z.string(),
  description: z.string(),
  status: z.string(),
  resolved: z.boolean(),
  runAt: z.number().describe("Unix timestamp (ms) of the scheduled run."),
  nextRunAt: z.number().nullable().optional().describe(
    "Unix timestamp (ms) of the next occurrence for recurring tasks.",
  ),
  createdAt: z.number(),
  preferredNetwork: z.string(),
  targetConversationId: z.string().nullable().optional(),
  aliceChatUserId: z.string().nullable().optional(),
  recurrenceRule: z.string().nullable().optional(),
  recurrenceEnd: z.string().nullable().optional(),
  subject: z.string().nullable().optional(),
});

export const pauseInteractionInputSchema = z
  .object({
    ...botAuthFields,
    targetConversationId: z.string().describe(
      "The target conversation ID to pause or resume interaction for.",
    ),
    resume: z.boolean().optional().describe(
      "If true, resumes interaction for the conversation. If false or omitted, pauses interaction.",
    ),
    time: z.string().optional().describe(
      "Optional pause duration (e.g. '10 minutes', '1 hour', '24 hours', '7 days', or milliseconds string like '3600000'). Automatically resumes after this duration.",
    ),
  })
  .refine(requireBotAuth, botAuthRefineConfig);

export const takeoverConversationInputSchema = z
  .object({
    ...botAuthFields,
    targetConversationId: z.string().describe(
      "The target conversation ID to take over / mute bot for.",
    ),
    time: z.string().optional().describe(
      "Optional takeover duration (e.g. '24 hours', '1 hour'). Defaults to '24 hours'.",
    ),
  })
  .refine(requireBotAuth, botAuthRefineConfig);

export const handoffReplyInputSchema = z.object({
  secret: z.string().describe("Ephemeral handoff secret token"),
  botId: z.string().describe("Target bot ID"),
  targetConversationId: z.string().describe("Target conversation ID"),
  text: z.string().min(1).describe("Reply text from human operator"),
  attachments: z.array(z.any()).optional().describe("Optional attachments"),
});

export const endHandoffInputSchema = z
  .object({
    secret: z.string().optional().describe("Ephemeral handoff secret token"),
    apiToken: z.string().optional().describe("Admin API token"),
    botId: z.string().describe("Target bot ID"),
    targetConversationId: z.string().describe("Target conversation ID"),
  })
  .refine(
    ({ secret, apiToken }) => Boolean(secret || apiToken),
    "Either secret (handoff token) or apiToken must be provided",
  );

export const getHandoffConversationInputSchema = z
  .object({
    secret: z.string().optional().describe("Ephemeral handoff secret token"),
    apiToken: z.string().optional().describe("Admin API token"),
    botId: z.string().describe("Target bot ID"),
    targetConversationId: z.string().describe("Target conversation ID"),
  })
  .refine(
    ({ secret, apiToken }) => Boolean(secret || apiToken),
    "Either secret (handoff token) or apiToken must be provided",
  );

export const handoffMessageSchema = z.object({
  id: z.string(),
  from: z.string(),
  text: z.string(),
  time: z.number(),
  isOwn: z.boolean().optional(),
});

export type HandoffMessage = z.infer<typeof handoffMessageSchema>;

export const apiDefinition = {
  "pause-interaction": endpoint({
    authRequired: false,
    input: pauseInteractionInputSchema,
    output: z.union([
      z.object({ success: z.literal(true), paused: z.boolean() }),
      z.object({ success: z.literal(false), error: z.string() }),
    ]),
  }),
  "takeover-conversation": endpoint({
    authRequired: false,
    input: takeoverConversationInputSchema,
    output: z.union([
      z.object({
        success: z.literal(true),
        secret: z.string(),
        expiresAt: z.number(),
      }),
      z.object({ success: z.literal(false), error: z.string() }),
    ]),
  }),
  "handoff-reply": endpoint({
    authRequired: false,
    input: handoffReplyInputSchema,
    output: z.union([
      z.object({ success: z.literal(true) }),
      z.object({ success: z.literal(false), error: z.string() }),
    ]),
  }),
  "end-handoff": endpoint({
    authRequired: false,
    input: endHandoffInputSchema,
    output: z.union([
      z.object({ success: z.literal(true) }),
      z.object({ success: z.literal(false), error: z.string() }),
    ]),
  }),
  "get-handoff-conversation": endpoint({
    authRequired: false,
    input: getHandoffConversationInputSchema,
    output: z.union([
      z.object({
        success: z.literal(true),
        isHandoffActive: z.boolean(),
        expiresAt: z.number().optional(),
        messages: z.array(handoffMessageSchema),
      }),
      z.object({ success: z.literal(false), error: z.string() }),
    ]),
  }),
  "create-remote-task": endpoint({
    authRequired: false,
    input: createRemoteActionInputSchema,
    output: z.union([
      z.object({
        success: z.literal(true),
        taskId: z.string(),
        conversationId: z.string(),
        runAt: z.number(),
        message: z.string(),
      }),
      z.object({ success: z.literal(false), error: z.string() }),
    ]),
  }),
  "list-scheduled-tasks": endpoint({
    authRequired: false,
    input: z.object({
      ...botAuthFields,
      includeResolved: z.boolean().optional().describe(
        "Include resolved and cancelled tasks. Defaults to false (only pending tasks are returned).",
      ),
    }).refine(requireBotAuth, botAuthRefineConfig),
    output: z.union([
      z.object({
        success: z.literal(true),
        tasks: z.array(scheduledTaskSchema),
      }),
      z.object({ success: z.literal(false), error: z.string() }),
    ]),
  }),
  "delete-scheduled-task": endpoint({
    authRequired: false,
    input: z.object({
      ...botAuthFields,
      taskId: z.string().describe(
        "ID of the scheduled task to delete (obtained from list-scheduled-tasks). Deleting cancels the task, including all future occurrences of a recurring task.",
      ),
    }).refine(requireBotAuth, botAuthRefineConfig),
    output: z.union([
      z.object({ success: z.literal(true) }),
      z.object({ success: z.literal(false), error: z.string() }),
    ]),
  }),
  "inject-reply": endpoint({
    authRequired: false,
    input: injectReplyInputSchema,
    output: z.union([
      z.object({ success: z.literal(true) }),
      z.object({ success: z.literal(false), error: z.string() }),
    ]),
  }),
  "inject-context": endpoint({
    authRequired: false,
    input: injectContextInputSchema,
    output: z.union([
      z.object({ success: z.literal(true) }),
      z.object({ success: z.literal(false), error: z.string() }),
    ]),
  }),
  "set-custom-tools": endpoint({
    authRequired: false,
    input: z.object({
      ...botAuthFields,
      tools: z.array(remoteToolSchema).optional().describe(
        "Custom HTTP tool endpoints the bot can call. Each tool has a name, description, URL, and JSON Schema parameters.",
      ),
      skills: z.array(remoteSkillSchema).optional().describe(
        "Remote skills grouping related tools. Each skill has a name, description, instructions, and an array of tools.",
      ),
    }).refine(requireBotAuth, botAuthRefineConfig),
    output: z.union([
      z.object({ success: z.literal(true) }),
      z.object({ success: z.literal(false), error: z.string() }),
    ]),
  }),
  "set-prompt": endpoint({
    authRequired: false,
    input: z.object({ ...botAuthFields, prompt: z.string() }).refine(
      requireBotAuth,
      botAuthRefineConfig,
    ),
    output: z.union([
      z.object({ success: z.literal(true) }),
      z.object({ success: z.literal(false), error: z.string() }),
    ]),
  }),
  "run-agent-test": endpoint({
    authRequired: false,
    input: z.object({
      ...botAuthFields,
      test: z.string().describe("The natural-language assertion to verify."),
    }).refine(requireBotAuth, botAuthRefineConfig),
    // The test runs asynchronously as a background job (the multi-turn
    // simulation chains many LLM calls and would exceed the request timeout).
    // Returns a testRunId to poll with get-agent-test-result.
    output: z.union([
      z.object({ testRunId: z.string() }),
      z.object({ success: z.literal(false), error: z.string() }),
    ]),
  }),
  "get-agent-test-result": endpoint({
    authRequired: false,
    input: z.object({
      ...botAuthFields,
      testRunId: z.string().describe(
        "The testRunId returned by run-agent-test.",
      ),
    }).refine(requireBotAuth, botAuthRefineConfig),
    output: z.union([
      z.object({ status: z.enum(["pending", "running"]) }),
      z.object({ status: z.literal("done"), outcome: agentTestOutcomeSchema }),
      z.object({ status: z.literal("error"), error: z.string() }),
    ]),
  }),
  "create-progress-bar": endpoint({
    authRequired: false,
    input: z.object({
      ...botAuthFields,
      conversationId: z.string(),
      network: z.string(),
      label: z.string(),
      type: z.enum(["progress", "spinner"]).optional(),
    }).refine(requireBotAuth, botAuthRefineConfig),
    output: z.union([
      z.object({
        success: z.literal(true),
        elementId: z.string(),
      }),
      z.object({ success: z.literal(false), error: z.string() }),
    ]),
  }),
  "ui-update": endpoint({
    authRequired: false,
    input: z.object({
      elementId: z.string(),
      active: z.boolean().optional(),
      percentage: z.number().optional(),
    }),
    output: z.union([
      z.object({ success: z.literal(true) }),
      z.object({ success: z.literal(false), error: z.string() }),
    ]),
  }),
  "set-supergreen-whatsapp": endpoint({
    authRequired: false,
    input: z.object({
      ...botAuthFields,
      whatsappNumber: z.string().describe(
        "The WhatsApp number from your Supergreen account (digits only)",
      ),
      secretToken: z.string().describe(
        "The secret token from your Supergreen account",
      ),
    }).refine(requireBotAuth, botAuthRefineConfig),
    output: z.union([
      z.object({ success: z.literal(true) }),
      z.object({ success: z.literal(false), error: z.string() }),
    ]),
  }),
  "set-agentmail-email": endpoint({
    authRequired: false,
    input: z.object({
      ...botAuthFields,
      apiKey: z.string(),
    }).refine(requireBotAuth, botAuthRefineConfig),
    output: z.union([
      z.object({ success: z.literal(true), address: z.string() }),
      z.object({ success: z.literal(false), error: z.string() }),
    ]),
  }),
  "set-resend-email": endpoint({
    authRequired: false,
    input: z.object({
      ...botAuthFields,
      apiKey: z.string(),
      fromAddress: z.string(),
      webhookSecret: z.string().optional(),
    }).refine(requireBotAuth, botAuthRefineConfig),
    output: z.union([
      z.object({ success: z.literal(true) }),
      z.object({ success: z.literal(false), error: z.string() }),
    ]),
  }),
  "rotate-webhook-secret": endpoint({
    authRequired: false,
    input: z.object({
      ...botAuthFields,
    }).refine(requireBotAuth, botAuthRefineConfig),
    output: z.union([
      z.object({ success: z.literal(true), webhookSecret: z.string() }),
      z.object({ success: z.literal(false), error: z.string() }),
    ]),
  }),
  "get-bot-info": endpoint({
    authRequired: false,
    input: z.object({
      ...botAuthFields,
    }).refine(requireBotAuth, botAuthRefineConfig),
    output: z.union([
      z.object({
        success: z.literal(true),
        botId: z.string(),
        name: z.string(),
        aliceAndBotPublicId: z.string(),
        prompt: z.string().optional(),
        tools: z.array(z.object({
          name: z.string(),
          url: z.string(),
          description: z.string().optional(),
          parameters: z.any().optional(),
        })).optional(),
      }),
      z.object({ success: z.literal(false), error: z.string() }),
    ]),
  }),
  "create-whatsapp-template": endpoint({
    authRequired: false,
    input: z.object({
      ...botAuthFields,
      name: z.string().describe(
        "Template name. Lowercase letters, numbers, and underscores only (e.g. order_confirmation).",
      ),
      language: z.string().describe(
        "Language code (e.g. en_US, es_ES, he). Each language must be submitted separately.",
      ),
      category: z.enum(["MARKETING", "UTILITY", "AUTHENTICATION"]).describe(
        "MARKETING: promotions/offers. UTILITY: transactional updates (order status, reminders). AUTHENTICATION: one-time passwords.",
      ),
      components: z.array(whatsappTemplateComponentSchema).describe(
        "Template components. BODY is required. HEADER, FOOTER, BUTTONS are optional.",
      ),
    }).refine(requireBotAuth, botAuthRefineConfig),
    output: z.union([
      z.object({
        success: z.literal(true),
        name: z.string(),
        language: z.string(),
        status: z.string(),
        id: z.string().optional(),
      }),
      z.object({ success: z.literal(false), error: z.string() }),
    ]),
  }),
  "check-whatsapp-template-status": endpoint({
    authRequired: false,
    input: z.object({
      ...botAuthFields,
      name: z.string().describe("Template name to look up."),
    }).refine(requireBotAuth, botAuthRefineConfig),
    output: whatsappTemplateResultSchema,
  }),
  "list-whatsapp-templates": endpoint({
    authRequired: false,
    input: z.object({ ...botAuthFields }).refine(
      requireBotAuth,
      botAuthRefineConfig,
    ),
    output: whatsappTemplateResultSchema,
  }),
  "delete-whatsapp-template": endpoint({
    authRequired: false,
    input: z.object({
      ...botAuthFields,
      name: z.string().describe("Template name to delete from the WABA."),
    }).refine(requireBotAuth, botAuthRefineConfig),
    output: z.union([
      z.object({ success: z.literal(true) }),
      z.object({ success: z.literal(false), error: z.string() }),
    ]),
  }),
  "create-bot-api": endpoint({
    authRequired: false,
    input: z.object({
      apiToken: z.string(),
      name: z.string(),
      prompt: z.string(),
      inlineSkills: z.array(remoteSkillSchema).optional().describe(
        "Inline remote skills with custom HTTP tool endpoints. Each skill groups related tools under a name, description, and instructions.",
      ),
      skills: z.array(z.string()).optional().describe(
        "Community skill source URLs (npm package names or GitHub URLs pointing to a SKILL.md). Validated before bot creation.",
      ),
      secrets: z.array(
        z.object({
          name: z.string().describe("Secret name (e.g. 'github-token')."),
          value: z.string().describe("The plaintext secret value."),
          hosts: z.array(z.string()).describe(
            "Hostnames the secret is scoped to (e.g. ['api.github.com']).",
          ),
        }),
      ).optional().describe(
        "Encrypted secrets scoped to specific hostnames for outbound API access from VMs.",
      ),
      isolateUsers: z.boolean().optional().describe(
        "When true, each conversation gets its own isolated context — cross-user tools (contacts, active conversations, scheduling, OAuth) are removed and Google OAuth tokens are scoped per-conversation. Defaults to true.",
      ),
      enableCodeExecution: z.boolean().optional().describe(
        "When true, the bot can run code in sandboxes and create/manage virtual machines. Defaults to true.",
      ),
      enableImageGeneration: z.boolean().optional().describe(
        "When true, the bot can generate images from text prompts via Google Imagen. Defaults to true.",
      ),
      enableWebSearch: z.boolean().optional().describe(
        "When true, the bot can search the web using Brave Search. Defaults to true.",
      ),
      enableBrowserUse: z.boolean().optional().describe(
        "When true, the bot can use cloud browsers to interact with web pages. Defaults to true.",
      ),
      showToolUseSpinner: z.boolean().optional().describe(
        "When true, users see a spinner while a tool call is running. Defaults to false.",
      ),
    }),
    output: z.union([
      z.object({
        success: z.literal(true),
        botId: z.string(),
        chatLink: z.string(),
        conversationsLink: z.string(),
        viewChatGroupId: z.string(),
        aliceAndBotPublicId: z.string(),
      }),
      z.object({ success: z.literal(false), error: z.string() }),
    ]),
  }),
  "delete-bot-api": endpoint({
    authRequired: false,
    input: z.object({
      apiToken: z.string().describe("Your p2b_ API token (from Settings)."),
      botId: z.string().describe("The ID of the bot to delete."),
      deleteViewChat: z.boolean().optional().describe(
        "Also delete the associated view-chat conversations.",
      ),
    }),
    output: z.union([
      z.object({ success: z.literal(true) }),
      z.object({ success: z.literal(false), error: z.string() }),
    ]),
  }),
  "create-guest": endpoint({
    authRequired: false,
    input: z.object({
      attribution: z.string().optional().describe(
        "Attribution tag identifying the 3rd-party platform that created this guest (e.g. 'my-skill-marketplace').",
      ),
    }),
    output: z.union([
      z.object({
        success: z.literal(true),
        token: z.string(),
        aliceAndBotCredentials: z.object({
          publicSignKey: z.string(),
          privateSignKey: z.string(),
          privateEncryptKey: z.string(),
        }),
      }),
      z.object({ success: z.literal(false), error: z.string() }),
    ]),
  }),
  "login-with-token": endpoint({
    authRequired: false,
    input: z.object({
      token: z.string().describe("The p2b_ API token for the guest user."),
    }),
    output: z.union([
      z.object({ success: z.literal(true), refreshToken: z.string() }),
      z.object({ success: z.literal(false), error: z.string() }),
    ]),
  }),
  "list-bots": endpoint({
    authRequired: false,
    input: z.object({
      apiToken: z.string().describe(
        "Your p2b_ API token (from Settings).",
      ),
      limit: z.number().optional().describe(
        "Maximum number of bots to return (1–100, default 50).",
      ),
      offset: z.number().optional().describe(
        "Number of bots to skip (for pagination, default 0).",
      ),
    }),
    output: z.union([
      z.object({
        success: z.literal(true),
        bots: z.array(z.object({
          id: z.string(),
          name: z.string(),
          chatLink: z.string(),
          aliceAndBotPublicId: z.string(),
        })),
        nextOffset: z.number().nullable().describe(
          "Pass as offset in the next request to fetch more. Null when no more pages.",
        ),
      }),
      z.object({ success: z.literal(false), error: z.string() }),
    ]),
  }),
} as const;

export type ApiDefinition = typeof apiDefinition;

export type RemoteTool = z.infer<typeof remoteToolSchema>;
export type RemoteSkill = z.infer<typeof remoteSkillSchema>;
export type CreateRemoteTaskInput = z.infer<
  typeof createRemoteActionInputSchema
>;
export type InjectContextInput = z.infer<typeof injectContextInputSchema>;
export type InjectReplyInput = z.infer<typeof injectReplyInputSchema>;

export type SignedToolRequest<T> = {
  payload: {
    meta: {
      botId: string;
      conversationId: string;
      userId: string;
      network: string;
      timestamp: string;
      nonce: string;
      isGroupChat: boolean;
      toolCallId?: string;
      channel?: { network: string; id: string };
    };
    params: T;
  };
  signature: string;
};
